<template>
	<Teleport to="#floating-filter-panel-target">
		<section ref="panel" class="floating-filter-panel" role="dialog" :aria-label="title">
			<header>
				<h3>{{ title }}</h3>
				<div class="actions">
					<slot name="header-actions" />
					<button
						type="button"
						class="icon-button"
						:aria-label="t('filters.close')"
						:title="t('filters.close')"
						@click="$emit('close')"
					>
						<AppIcon :name="IconName.Close" class="close-icon" />
					</button>
				</div>
			</header>
			<div class="content">
				<slot />
			</div>
		</section>
	</Teleport>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core"
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

defineProps<{
	title: string
}>()

const emit = defineEmits(["close"])

const { t } = useI18n()

const panel = ref<HTMLElement | null>(null)
onClickOutside(panel, () => emit("close"), {
	ignore: [".popover-content", ".date-custom-panel", ".filter-button"],
})
</script>

<style scoped>
.floating-filter-panel {
	position: fixed;
	top: var(--spacing-xl);
	max-height: calc(100vh - (var(--spacing-xl) * 2));
	left: calc(var(--current-nav-width) + var(--width-left-panel) + var(--spacing-sm));
	transition: left 0.3s ease;
	z-index: 11;
	width: var(--width-filtering-panel);
	display: flex;
	flex-direction: column;
	background-color: var(--neutral-0);
	border-radius: var(--border-radius-lg);
	box-shadow: var(--shadow-xl);
	overflow: hidden;

	header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--spacing-md) var(--spacing-xl);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		h3 {
			margin: 0;
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xxs);
		}
	}

	.icon-button {
		width: var(--size-button);
		height: var(--size-button);
		border: none;
		background: transparent;
		color: var(--neutral-600);
		cursor: pointer;
		padding: var(--spacing-xs);
		display: inline-flex;
		align-items: center;
		justify-content: center;

		&:hover {
			color: var(--neutral-900);
		}

		.close-icon {
			width: var(--size-icon-lg);
			height: var(--size-icon-lg);
			stroke: currentColor;
			fill: none;
			stroke-width: 2;
			stroke-linecap: round;
		}
	}

	.content {
		flex-grow: 1;
		min-height: 0;
		overflow-y: auto;
		padding: var(--spacing-md) var(--spacing-xl);
	}
}
</style>
